// src/features/audittrail/logic.ts
// Pure helpers shared across the audittrail feature — no React imports.

import type { Entity } from '@core/types/entity';
import type { PaginatedRequest } from '@core/types/pagination';

/** Build a well-formed PaginatedRequest — sort/view are required fields on this type. */
export function paginationParams(limit = 50): PaginatedRequest {
  return { pagination: { cursor: '', limit, direction: 'forward' }, sort: [], view: 0 };
}

export interface ScopeIds {
  platformId: string;
  productId: string;
  tenantId: string;
}

/** Build a fully-populated Entity reference from an id and the current scope. */
export function makeEntity(id: string, typeName: string, scope: ScopeIds): Entity {
  return {
    type_id: '',
    type_name: typeName,
    id,
    platform_id: scope.platformId,
    tenant_id: scope.tenantId,
    product_id: scope.productId,
  };
}

export function errMessage(e: unknown, fallback: string): string {
  if (e instanceof Error && e.message) return e.message;
  return fallback;
}

export function fmtDateTime(value: string | undefined, locale = 'en'): string {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString(locale, {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return value;
  }
}

export function fmtDate(value: string | undefined, locale = 'en'): string {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString(locale, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return value;
  }
}

/** Strip an ENUM_PREFIX_ off a proto enum value and lowercase it for i18n keys. */
export function enumKey(value: string | undefined, prefix: string): string {
  if (!value) return 'unspecified';
  return value.startsWith(prefix) ? value.slice(prefix.length).toLowerCase() : value.toLowerCase();
}

export const AUDIT_ACTION_VALUES = [
  'AUDIT_ACTION_CREATE', 'AUDIT_ACTION_READ', 'AUDIT_ACTION_UPDATE', 'AUDIT_ACTION_DELETE',
  'AUDIT_ACTION_LOGIN', 'AUDIT_ACTION_LOGOUT', 'AUDIT_ACTION_EXPORT', 'AUDIT_ACTION_SHARE',
  'AUDIT_ACTION_APPROVE', 'AUDIT_ACTION_REJECT', 'AUDIT_ACTION_ESCALATE',
] as const;

export const DATA_CLASSIFICATION_VALUES = [
  'DATA_CLASSIFICATION_PUBLIC', 'DATA_CLASSIFICATION_INTERNAL', 'DATA_CLASSIFICATION_CONFIDENTIAL',
  'DATA_CLASSIFICATION_RESTRICTED', 'DATA_CLASSIFICATION_PII', 'DATA_CLASSIFICATION_SENSITIVE_PII',
] as const;

export const COMPLIANCE_FRAMEWORK_VALUES = [
  'COMPLIANCE_FRAMEWORK_GDPR', 'COMPLIANCE_FRAMEWORK_SOX', 'COMPLIANCE_FRAMEWORK_FZ_152',
  'COMPLIANCE_FRAMEWORK_LOCAL', 'COMPLIANCE_FRAMEWORK_CUSTOM',
] as const;

export const PURGE_STRATEGY_VALUES = ['PURGE_STRATEGY_HARD_DELETE', 'PURGE_STRATEGY_ANONYMIZE', 'PURGE_STRATEGY_ARCHIVE'] as const;

export const COMPLIANCE_REPORT_TYPE_VALUES = [
  'COMPLIANCE_REPORT_TYPE_GDPR_ARTICLE_30', 'COMPLIANCE_REPORT_TYPE_SOX',
  'COMPLIANCE_REPORT_TYPE_ACCESS_LOG', 'COMPLIANCE_REPORT_TYPE_DATA_BREACH', 'COMPLIANCE_REPORT_TYPE_CUSTOM',
] as const;

export const DSAR_TYPE_VALUES = [
  'DSAR_TYPE_ACCESS', 'DSAR_TYPE_RECTIFICATION', 'DSAR_TYPE_ERASURE', 'DSAR_TYPE_PORTABILITY', 'DSAR_TYPE_RESTRICTION',
] as const;
